<script lang="ts">
  /**
   * Camera QR scanner (Phase 5 — SCAFFOLDING). Decodes a device's E2EE key QR
   * via `jsqr`, then (in the parent) re-encrypts the key to the student's own
   * public key and persists it. Only mounted when the E2EE flag is on.
   *
   * Emits `scan` with the raw decoded text. The parent owns crypto + storage.
   */
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import jsQR from "jsqr";
  import { t } from "./i18n";

  const dispatch = createEventDispatcher<{ scan: string; close: void }>();

  let video: HTMLVideoElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  let stream: MediaStream | null = null;
  let raf = 0;
  let error: string | null = null;
  let scanning = false;

  export async function start(): Promise<void> {
    error = null;
    try {
      try {
        // Prefer the rear camera, but use `{ ideal: "environment" }` (a soft
        // hint, not a hard string). A hard `facingMode: "environment"` REJECTS
        // on desktops/laptops that only have a front ("user") camera; `ideal`
        // lets the browser fall back to whatever camera exists instead.
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch {
        // Some browsers/devices still reject the soft hint. Retry once with the
        // most permissive constraint so any single camera (e.g. a desktop's
        // only "user" cam) is accepted.
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      scanning = true;
      tick();
    } catch {
      // Only surface the error when BOTH the ideal-environment attempt and the
      // plain-video fallback failed (e.g. permission denied, no camera).
      error = t("e2ee.scan.cameraError");
    }
  }

  function tick(): void {
    if (!scanning || !video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w > 0 && h > 0) {
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.drawImage(video, 0, 0, w, h);
        const img = ctx.getImageData(0, 0, w, h);
        const found = jsQR(img.data, w, h);
        if (found && found.data) {
          dispatch("scan", found.data);
          stop();
          return;
        }
      }
    }
    raf = requestAnimationFrame(tick);
  }

  export function stop(): void {
    scanning = false;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    if (stream) {
      for (const tr of stream.getTracks()) tr.stop();
      stream = null;
    }
  }

  // Auto-start the camera as soon as the scanner mounts so the QR is live
  // immediately. The Start/Stop buttons remain as a manual fallback (e.g. after
  // a permission prompt is re-granted). A getUserMedia rejection surfaces via
  // the existing `error` UI inside start().
  onMount(() => {
    void start();
  });

  // Release the camera track on unmount/destroy.
  onDestroy(stop);
</script>

<div class="qr-scanner">
  <video bind:this={video} playsinline muted></video>
  <canvas bind:this={canvas} hidden></canvas>
  {#if error}
    <p class="qr-error">{error}</p>
  {/if}
  <div class="qr-actions">
    {#if !scanning}
      <button type="button" on:click={start}>{t("e2ee.scan.start")}</button>
    {:else}
      <button type="button" on:click={stop}>{t("e2ee.scan.stop")}</button>
    {/if}
    <button type="button" on:click={() => dispatch("close")}>
      {t("e2ee.scan.close")}
    </button>
  </div>
</div>

<style>
  .qr-scanner {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
  video {
    width: 100%;
    max-width: 320px;
    border-radius: 8px;
    background: #000;
  }
  .qr-error {
    color: #d33;
    font-size: 0.9rem;
  }
  .qr-actions {
    display: flex;
    gap: 0.5rem;
  }
</style>
